<!-- A money-over-time line, with an optional tint fill beneath it. Shared by any screen plotting a series of dated amounts. -->
<script setup lang="ts">
import {
  compareMoney,
  daysBetween,
  formatDate,
  formatMoney,
  isZeroMoney,
  type LocalDate,
  type Money,
  moneyProportion,
  niceMoneyTicks,
  plusDays,
  zeroMoney,
} from '@taler/domain'
import { computed } from 'vue'

export interface MoneyChartPoint {
  readonly date: LocalDate
  readonly value: Money
}

const props = withDefaults(
  defineProps<{
    points: readonly MoneyChartPoint[]
    label: string
    locale: 'en' | 'hu'
    area?: boolean
  }>(),
  { area: false },
)

const VIEW_WIDTH = 960
const VIEW_HEIGHT = 280
const PAD_LEFT = 76
const PAD_RIGHT = 12
const PAD_TOP = 16
const PAD_BOTTOM = 32
const PLOT_WIDTH = VIEW_WIDTH - PAD_LEFT - PAD_RIGHT
const PLOT_HEIGHT = VIEW_HEIGHT - PAD_TOP - PAD_BOTTOM
const TARGET_Y_TICKS = 5
const X_TICK_FRACTIONS = [0, 0.25, 0.5, 0.75, 1] as const

const first = computed(() => props.points[0])
const last = computed(() => props.points.at(-1))
const currency = computed(() => first.value?.value.currency)

interface Bounds {
  readonly min: Money
  readonly max: Money
}

const yTicks = computed(() => {
  const currencyCode = currency.value
  if (currencyCode === undefined) return []
  const values: Money[] = [zeroMoney(currencyCode), ...props.points.map((point) => point.value)]
  const min = values.reduce((min, value) => (compareMoney(value, min) < 0 ? value : min))
  const max = values.reduce((max, value) => (compareMoney(value, max) > 0 ? value : max))
  return niceMoneyTicks(min, max, TARGET_Y_TICKS)
})

const bounds = computed<Bounds | undefined>(() => {
  const ticks = yTicks.value
  if (ticks.length === 0) return undefined
  return { min: ticks[0]!, max: ticks[ticks.length - 1]! }
})

const dateSpanDays = computed(() => {
  if (first.value === undefined || last.value === undefined) return 1
  return Math.max(1, daysBetween(first.value.date, last.value.date))
})

function chartX(date: LocalDate): number {
  if (first.value === undefined) return PAD_LEFT
  const fraction = daysBetween(first.value.date, date) / dateSpanDays.value
  return PAD_LEFT + fraction * PLOT_WIDTH
}

function chartY(value: Money): number {
  const range = bounds.value
  if (range === undefined) return PAD_TOP + PLOT_HEIGHT
  const fraction = moneyProportion(value, range.min, range.max)
  return PAD_TOP + PLOT_HEIGHT - fraction * PLOT_HEIGHT
}

const linePath = computed(() =>
  props.points
    .map((point, index) => `${index === 0 ? 'M' : 'L'}${chartX(point.date)},${chartY(point.value)}`)
    .join(' '),
)

const areaPath = computed(() => {
  if (props.points.length === 0) return ''
  const baseline = PAD_TOP + PLOT_HEIGHT
  const firstX = chartX(props.points[0]!.date)
  const lastX = chartX(props.points[props.points.length - 1]!.date)
  return `${linePath.value} L${lastX},${baseline} L${firstX},${baseline} Z`
})

const yAxisTicks = computed(() =>
  yTicks.value.map((value) => ({
    y: chartY(value),
    label: formatMoney(value, { locale: props.locale, showSymbol: false }),
    isZero: isZeroMoney(value),
  })),
)

const xTicks = computed(() => {
  const start = first.value
  if (start === undefined) return []
  const span = dateSpanDays.value
  return X_TICK_FRACTIONS.map((fraction) => {
    const date = plusDays(start.date, Math.round(fraction * span))
    return { x: chartX(date), label: formatDate(date, props.locale), fraction }
  })
})
</script>

<template>
  <svg
    v-if="first && last"
    role="img"
    :aria-label="label"
    :viewBox="`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`"
    preserveAspectRatio="none"
    class="h-64 w-full"
  >
    <g v-for="tick in yAxisTicks" :key="`y-${tick.y}`">
      <line
        :x1="PAD_LEFT"
        :x2="VIEW_WIDTH - PAD_RIGHT"
        :y1="tick.y"
        :y2="tick.y"
        :stroke="tick.isZero ? 'var(--color-text-muted)' : 'var(--color-border)'"
        :stroke-width="tick.isZero ? 1.5 : 1"
      />
      <text
        :x="PAD_LEFT - 8"
        :y="tick.y"
        text-anchor="end"
        dominant-baseline="middle"
        :fill="tick.isZero ? 'var(--color-text)' : 'var(--color-text-muted)'"
        :font-weight="tick.isZero ? 600 : 400"
        font-size="10"
        data-axis="y"
      >
        {{ tick.label }}
      </text>
    </g>

    <path
      v-if="area"
      :d="areaPath"
      fill="color-mix(in srgb, var(--color-accent) 12%, transparent)"
      stroke="none"
    />

    <path
      :d="linePath"
      fill="none"
      stroke="var(--color-accent)"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />

    <g v-for="tick in xTicks" :key="`x-${tick.x}`">
      <text
        :x="tick.x"
        :y="VIEW_HEIGHT - PAD_BOTTOM + 18"
        :text-anchor="tick.fraction === 0 ? 'start' : tick.fraction === 1 ? 'end' : 'middle'"
        fill="var(--color-text-muted)"
        font-size="10"
        data-axis="x"
      >
        {{ tick.label }}
      </text>
    </g>
  </svg>
</template>
